import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Car, Calendar, Clock, MapPin, Euro } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookingResponse() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const action = searchParams.get('action'); // 'accept' or 'reject'

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, ready, success, error

  useEffect(() => {
    if (!bookingId || !token) {
      setStatus('error');
      setLoading(false);
      return;
    }

    fetchBooking();
  }, [bookingId, token]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/api/bookings/${bookingId}`);
      setBooking(response.data);
      
      // Check if booking is in correct state
      if (response.data.status !== 'alternative_proposed') {
        setStatus('invalid');
      } else {
        setStatus('ready');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching booking:', error);
      setStatus('error');
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/api/bookings/${bookingId}/accept-alternative?token=${token}`);
      setStatus('success');
      toast.success('Proposition acceptée ! Votre réservation est confirmée.');
    } catch (error) {
      console.error('Error accepting:', error);
      toast.error('Erreur lors de l\'acceptation');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/api/bookings/${bookingId}/reject-alternative?token=${token}`, {
        reason: "Client a refusé la proposition"
      });
      setStatus('rejected');
      toast.info('Proposition refusée. Votre réservation est annulée.');
    } catch (error) {
      console.error('Error rejecting:', error);
      toast.error('Erreur lors du refus');
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (status === 'error' || !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Lien Invalide</h2>
          <p className="text-gray-300 mb-6">
            Ce lien est invalide ou a expiré. Veuillez contacter notre service client.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-lg"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 text-center">
          <XCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Déjà Traité</h2>
          <p className="text-gray-300 mb-6">
            Cette proposition a déjà été traitée ou n'est plus disponible.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-lg"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Réservation Confirmée !</h2>
          <p className="text-gray-300 mb-2">
            Votre réservation avec <strong className="text-amber-500">{booking.proposedVehicle}</strong> est confirmée.
          </p>
          <p className="text-sm text-gray-400 mb-6">
            Vous recevrez un email de confirmation sous peu.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold rounded-lg"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Proposition Refusée</h2>
          <p className="text-gray-300 mb-6">
            Votre réservation a été annulée. Nous espérons vous revoir bientôt !
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-lg"
          >
            Nouvelle Réservation
          </button>
        </div>
      </div>
    );
  }

  // Ready state - show comparison and buttons
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full mb-4">
            <Car className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Proposition de Véhicule Alternatif
          </h1>
          <p className="text-gray-400">Réservation #{booking.id}</p>
        </div>

        {/* Booking Details */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">📅 Détails de votre réservation</h3>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              <span>{booking.date} à {booking.time}</span>
            </div>
            <div className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-amber-500" />
              <span>{booking.estimatedPrice}€</span>
            </div>
            <div className="flex items-start gap-2 md:col-span-2">
              <MapPin className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="text-sm text-gray-400">Prise en charge</p>
                <p>{booking.pickup}</p>
              </div>
            </div>
            {booking.dropoff && (
              <div className="flex items-start gap-2 md:col-span-2">
                <MapPin className="w-5 h-5 text-red-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-400">Destination</p>
                  <p>{booking.dropoff}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Comparison */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 mb-6">
          <h3 className="text-lg font-semibold text-white mb-6 text-center">🚗 Comparaison des Véhicules</h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Original Vehicle */}
            <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">❌</div>
              <p className="text-sm text-gray-400 mb-2">Véhicule demandé</p>
              <p className="text-xl font-bold text-red-400 mb-2">{booking.originalVehicle}</p>
              <p className="text-sm text-gray-500">Non disponible</p>
            </div>

            {/* Proposed Vehicle */}
            <div className="bg-green-900/20 border-2 border-green-500 rounded-lg p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <p className="text-sm text-gray-400 mb-2">Notre proposition</p>
              <p className="text-xl font-bold text-green-400 mb-2">{booking.proposedVehicle}</p>
              <p className="text-sm text-gray-500">Disponible immédiatement</p>
            </div>
          </div>

          {booking.adminNote && (
            <div className="mt-6 bg-blue-900/20 border border-blue-500/50 rounded-lg p-4">
              <p className="text-sm text-blue-300">
                <strong>📝 Note de notre équipe :</strong> {booking.adminNote}
              </p>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-amber-900/20 border border-amber-500/50 rounded-lg p-4 mb-6">
          <p className="text-amber-300 text-sm">
            <strong>💰 Prix :</strong> Inchangé - {booking.estimatedPrice}€<br />
            <strong>🎯 Catégorie :</strong> Équivalente ou supérieure
          </p>
        </div>

        {/* Action Buttons */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-8 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-6 text-center">Que décidez-vous ?</h3>
          
          <div className="space-y-4">
            <button
              onClick={handleAccept}
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-lg transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  J'accepte la proposition
                </>
              )}
            </button>

            <button
              onClick={handleReject}
              disabled={submitting}
              className="w-full py-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold rounded-lg transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  Je refuse (annulation)
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center mt-6">
            💡 Si vous acceptez, votre réservation sera confirmée avec le nouveau véhicule.<br />
            Si vous refusez, votre réservation sera annulée sans frais.
          </p>
        </div>
      </div>
    </div>
  );
}
